import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle, Search, Home, Calculator, Handshake } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

export default function BuyerDistrictPage({ config }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: config.h1,
    provider: {
      "@type": "RealEstateAgent",
      name: "BRIMO Immobilien NRW",
      telephone: "+4921293766829",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Friedrich-Ebert-Straße 76",
        addressLocality: "Haan",
        postalCode: "42781",
        addressCountry: "DE",
      },
    },
    areaServed: config.name,
    description: config.description,
  };

  useMeta(config.title, config.description);
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [config.name]);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end pb-16" data-testid="buyer-district-hero">
        <div className="absolute inset-0">
          <img
            src={config.heroImage}
            alt={config.h1}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <nav className="flex items-center gap-2 text-gray-500 text-xs mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#E61D38] transition-colors">Start</Link>
            <span>/</span>
            <Link to="/immobilie-kaufen-kreis-mettmann" className="hover:text-[#E61D38] transition-colors">Kaufen</Link>
            <span>/</span>
            <span className="text-gray-400">{config.name}</span>
          </nav>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {config.h1.toUpperCase()}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed mb-8">{config.about}</p>
          <a
            href="#buyer-district-form"
            className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors w-fit"
            data-testid="buyer-district-cta"
          >
            Suchprofil für {config.name} erstellen
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Market Info */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="buyer-district-market">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-5"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                IMMOBILIEN KAUFEN IN {config.name.toUpperCase()}
              </h2>
              <p className="text-gray-400 leading-relaxed text-sm mb-6">{config.marketInfo}</p>

              {/* Price range */}
              <div className="bg-[#0d0d0d] border border-white/5 p-6 mb-8 flex items-center gap-5">
                <div className="bg-[#E61D38] p-3 flex-shrink-0">
                  <Home size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">
                    Kaufpreise in {config.name}
                  </p>
                  <p className="text-white font-bold text-lg">{config.priceRange}</p>
                  <p className="text-gray-600 text-xs mt-1">Abhängig von Lage, Zustand & Typ</p>
                </div>
              </div>

              {/* Highlights */}
              <h3
                className="text-white font-bold text-sm uppercase tracking-wider mb-5"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Warum {config.name} kaufen?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {config.highlights?.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-[#262626] border border-white/5 p-4"
                    data-testid={`buyer-district-highlight-${i}`}
                  >
                    <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              {/* Buyer Services */}
              <h3
                className="text-white font-bold text-sm uppercase tracking-wider mb-5"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Unsere Leistungen für Käufer in {config.name}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: <Search size={18} className="text-[#E61D38]" />,
                    title: "Suchprofil",
                    desc: `Exklusiver Zugang zu Angeboten in ${config.name}`,
                  },
                  {
                    icon: <Calculator size={18} className="text-[#E61D38]" />,
                    title: "Preisbewertung",
                    desc: "Unabhängige Bewertung des Kaufpreises",
                  },
                  {
                    icon: <Handshake size={18} className="text-[#E61D38]" />,
                    title: "Kaufbegleitung",
                    desc: "Von Besichtigung bis Notartermin",
                  },
                  {
                    icon: <MapPin size={18} className="text-[#E61D38]" />,
                    title: "Lokale Expertise",
                    desc: `Tiefekenntnis von ${config.name} & Kreis Mettmann`,
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-[#262626] border border-white/5 p-5 hover:border-[#E61D38]/30 transition-colors"
                    data-testid={`buyer-service-${i}`}
                  >
                    <div className="mb-3">{item.icon}</div>
                    <p className="text-white font-bold text-xs uppercase tracking-wider mb-1.5">
                      {item.title}
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Other Buyer Districts */}
              <div className="mt-10">
                <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-4">
                  Weitere Städte für Käufer
                </h3>
                <div className="flex flex-wrap gap-3">
                  {cityConfig.districts
                    .filter((d) => d.slug !== config.slug)
                    .slice(0, 4)
                    .map((d) => (
                      <Link
                        key={d.slug}
                        to={`/immobilie-kaufen-${d.slug}`}
                        className="flex items-center gap-1.5 text-gray-400 text-sm hover:text-[#E61D38] transition-colors bg-[#262626] px-4 py-2 border border-white/5 hover:border-[#E61D38]/30"
                        data-testid={`other-buyer-district-${d.slug}`}
                      >
                        <MapPin size={10} className="text-[#E61D38]" />
                        {d.name}
                      </Link>
                    ))}
                </div>
              </div>
            </div>

            {/* Sidebar form */}
            <div id="buyer-district-form">
              <div className="sticky top-24">
                <LeadForm
                  formType="buyer"
                  title={`Suchprofil ${config.name}`}
                  ctaText="Suchprofil senden"
                  showBudget={true}
                  showPropertyType={true}
                  showDistrict={false}
                  pageSource={`/immobilie-kaufen-${config.slug}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
